import { memo, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
    UnitPicker,
} from "..";
import DashboardCreateSelectSensorModal from "../modal/SelectDevices";
import { displaySensorType } from "utils/functions";

import { lowerCase, omit, set, size } from "lodash";
import { DeviceSelected } from "types";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import {
    WidgetType,
    getChartSourceFromWidgetType,
} from "components/dashboard/utils/widgetConfig";
import { CHART_DUPLICATE_COLOR_MESSAGE } from "constant/messages";
import { ColorRangeEditor, ColorStop } from "../../shared/ColorRangeEditor";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const RANGE_COLORS: ColorStop[] = [
    [25, "#59BCFB"],
    [50, "#FFF157"],
    [75, "#FFA200"],
    [100, "#FF0000"],
];
const getOffsetsByRange = (range: number) => {
    if (range === 2) return [50, 100];
    if (range === 3) return [33, 66, 100];
    return [25, 50, 75, 100];
};
const normalizeOffsets = (stops: ColorStop[], range: number): ColorStop[] => {
    const offsets = getOffsetsByRange(range);
    return offsets.map((offset, index) => [
        offset,
        stops[index]?.[1] ?? RANGE_COLORS[index][1],
    ]);
};
const CreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Dial Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
            className: "flex-basis-220px",
            render: (data: any) => (
                <div className="text-nowrap text-white text-truncate">
                    {data.ldsuUID}
                </div>
            ),
        },
        {
            key: "unit",
            name: "Unit",
        },
    ],
    transform: (data: DeviceSelected, widgetType: WidgetType) => {
        const { source, chart } = getChartSourceFromWidgetType(widgetType);
        return {
            attributes: [
                {
                    color: data.color,
                    device_id: data.ldsuUID,
                    gateway_id: data.gatewayId,
                    name: data.sensorType,
                    said: data.said,
                    time_series: getMQTTQueryByDevice(data),
                    unit: data.unit,
                    show_alert: false,
                },
            ],
            source: source,
            name: data.chartName,
            chart: chart,
        };
    },
    validate() {
        const validates = this.data.map((item) => ({
            path: `[name="${item.deviceId}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        const validateColor = () => {
            const uniqueColors = Array.from(
                new Set(
                    this.data[0].color.map(([, color]: [number, string]) => lowerCase(color))
                )
            );
            return {
                isValid: uniqueColors.length === this.data[0].color.length,
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            };
        }

        return [...validates, validateColor()];
    },
};

const DialGrade = () => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<{
        [key: string]: DeviceSelected;
    }>({});
    const [rangeColor, setRangeColor] = useState(4);
    const [colors, setColors] = useState<ColorStop[]>(RANGE_COLORS);

    const [widgetName, setWidgetName] = useState("Gaude Grade");

    const defaultMin = 0;
    const defaultMax = 100;

    const chartData = useMemo(
        () => [
            {
                ...Object.values(selectedDevice)[0],
                color: colors.map(([offset, Color]) => [offset / 100, Color]),
                chartName: widgetName,
            },
        ],
        [selectedDevice, widgetName, colors]
    );

    useEffect(() => {
        if (size(selectedDevice) > 0) {
            useCreateDashboardStore.setState({
                createData: {
                    ...CreateData,
                    data: chartData,
                },
            });
        } else {
            useCreateDashboardStore.setState({
                createData: undefined,
            });
        }
    }, [selectedDevice, widgetName, colors, chartData]);

    const filterSensor = (d: DeviceSelected) => {
        return (
            d.format !== "boolean" &&
            d.sensorType.toLowerCase() !== "people count sensor"
        );
    };

    const onDeviceSelect = (devices: { [key: string]: DeviceSelected }) => {
        const selectedDevices = Object.values(devices)
            .map((item) => ({
                ...item,
                name: item.sensorName,
                alert: false,
            }))
            .pluck("deviceId");

        setSelectedDevice(selectedDevices);
        setRangeColor(4);
        setColors(RANGE_COLORS);
        setWidgetName(
            `${Object.values(devices)[0].sensorName} reading with grades`
        );
    };

    const onRemove = (device: DeviceSelected) => {
        setSelectedDevice((prev) => omit(prev, device.deviceId));
        setRangeColor(4);
        setColors(RANGE_COLORS);
    };

    const onChange = (
        device: DeviceSelected,
        changes: Partial<DeviceSelected>
    ) => {
        const device_ = { ...device, ...changes };
        setSelectedDevice((prev) => set(prev, device.deviceId, device_));
    };

    const onRangeChange = (range: number) => {
        const rangeNumber = Number(range);
        setRangeColor(rangeNumber);
        setColors((prev) => normalizeOffsets(prev.slice(0, rangeNumber), rangeNumber));
    };

    const onColorChange = (color: string, index: number) => {
        setColors((prev) =>
            prev.map((item, idx) => (idx === index ? [item[0], color] : item))
        );
    };

    const device = Object.values(selectedDevice)[0];

    return (
        <>
            <Row>
                <Col sm="12">
                    {device && (
                        <Form.Group>
                            <Form.Label htmlFor="widget-name">
                                Widget name
                            </Form.Label>
                            <Form.Control
                                name="widget-name"
                                id="widget-name"
                                type="text"
                                value={widgetName}
                                placeholder="Dial Name"
                                onChange={(event) =>
                                    setWidgetName(event.target.value)
                                }
                            />
                        </Form.Group>
                    )}

                    <div className="mt-2">
                        {!device && (
                            <Button
                                className="mr-2"
                                variant="primary"
                                onClick={() => setShowSelectModal(true)}
                            >
                                Select Sensor
                            </Button>
                        )}
                    </div>
                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevice}
                        onSave={(event, data) => onDeviceSelect(data)}
                        deviceType="Sensor"
                        multipleSelection={false}
                        filter={filterSensor}
                    />
                </Col>
            </Row>
            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    {device && (
                        <div className="no-checkbox table-head">
                            <Row>
                                <Col md={2}>Sensor</Col>
                                <Col md={2}>Type</Col>
                                <Col xs={12} md={2}>
                                    {GATEWAY_LABEL}
                                </Col>
                                <Col md={2}>{LDSU_UUID_LABEL}</Col>
                                <Col md={1}>Unit</Col>
                                <Col md={2}>Value Range</Col>
                                <Col sm={1}></Col>
                            </Row>
                        </div>
                    )}
                    {device && (
                        <div className="table-row bg-secondary">
                            <Row className="no-checkbox">
                                <Col
                                    md={2}
                                    className="mb-md-0 mb-1 text-truncate"
                                >
                                    <span title={device.sensorName}>
                                        {device.sensorName}
                                    </span>
                                </Col>
                                <Col md={2} className="text-truncate">
                                    {displaySensorType(device)}
                                </Col>
                                <Col
                                    xs={12}
                                    md={2}
                                    className="text-truncate mb-1 mb-md-0"
                                >
                                    <span title={device.gatewayName}>
                                        {device.gatewayName}
                                    </span>
                                </Col>
                                <Col md={2} className="text-truncate mb-md-1">
                                    <span title={device.ldsuUID}>
                                        {device.ldsuUID}
                                    </span>
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="mb-md-0 mb-1 mb-md-0"
                                >
                                    <UnitPicker
                                        data={[device.unit]}
                                        defaultValue={device.unit}
                                        onChange={(_event: any, unit: string) =>
                                            onChange(device, { unit: unit })
                                        }
                                    />
                                </Col>
                                <Col
                                    xs={12}
                                    md={2}
                                    className="mb-md-0 mb-1 mb-md-0"
                                >
                                    {device.min} - {device.max}
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="d-flex justify-content-end  mb-1 mb-md-0"
                                >
                                    <CloseOutlined
                                        onClick={() => onRemove(device)}
                                        className="cursor-pointer mr-2 text-primary-red-1"
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Col>
            </Row>
            {device && (
                <Row className="mt-4">
                    <Col sm={9} xl={7}>
                        <Form.Label htmlFor="color-range">Range</Form.Label>
                        <ColorRangeEditor
                            colors={colors}
                            range={rangeColor}
                            min={device.min ?? defaultMin}
                            max={device.max ?? defaultMax}
                            onRangeChange={onRangeChange}
                            onColorChange={onColorChange}
                            onStopsChange={(nextColors) => setColors(nextColors)}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default memo(DialGrade);
